import React, { useState, useEffect } from 'react';
import { hrmAPI, financeAPI, inventoryAPI, crmAPI, vendorAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
    Users, Briefcase, Package, DollarSign, ShoppingCart,
    TrendingUp, TrendingDown, RefreshCw, Download, Building2,
    ClipboardList, CreditCard, Truck, Store, Calendar, FileText,
    ArrowRight, Activity, BarChart3, PieChart, Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { exportToExcel, exportToPDF } from '../lib/export';

const StatCard = ({ title, value, icon: Icon, trend, color, link }) => (
    <Link to={link} className="block">
        <div className="flex items-center p-4 bg-white dark:bg-slate-700 rounded-lg shadow-sm border border-gray-100 dark:border-slate-600 hover:shadow-md transition-shadow">
            <div className={`p-3 rounded-md mr-4 ${color}`}>
                <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{value}</p>
                <p className="text-sm text-gray-500 dark:text-gray-300">{title}</p>
                {trend !== undefined && (
                    <div className={`flex items-center text-xs mt-1 ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        {trend > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : trend < 0 ? <TrendingDown className="w-3 h-3 mr-1" /> : null}
                        {trend !== 0 ? `${Math.abs(trend)}% from last month` : 'No change'}
                    </div>
                )}
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
        </div>
    </Link>
);

const ModuleCard = ({ title, icon: Icon, color, modules }) => (
    <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-5">
        <div className="flex items-center mb-4">
            <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
        </div>
        <div className="space-y-2">
            {modules.map((module, idx) => (
                <Link
                    key={idx}
                    to={module.link}
                    className="flex items-center justify-between p-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 rounded-lg transition-colors"
                >
                    <span className="flex items-center">
                        {module.icon && <module.icon className="w-4 h-4 mr-2" />}
                        {module.name}
                    </span>
                    <span className="bg-gray-100 dark:bg-slate-600 px-2 py-0.5 rounded-full text-xs">
                        {module.count}
                    </span>
                </Link>
            ))}
        </div>
    </div>
);

const QuickActionButton = ({ icon: Icon, label, link, color }) => (
    <Link
        to={link}
        className={`flex flex-col items-center justify-center p-4 rounded-lg ${color} hover:opacity-90 transition-opacity`}
    >
        <Icon className="w-6 h-6 mb-2" />
        <span className="text-sm font-medium">{label}</span>
    </Link>
);

const Dashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        employees: 0,
        departments: 0,
        designations: 0,
        branches: 0,
        clients: 0,
        projects: 0,
        lifts: 0,
        vendors: 0,
        products: 0,
        stores: 0,
        chartOfAccounts: 0,
        bankAccounts: 0,
        journalEntries: 0,
    });
    const [recentActivities, setRecentActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            
            // HRM Data
            const [empRes, deptRes, desigRes, branchRes, attendRes, leaveRes] = await Promise.all([
                hrmAPI.getEmployees(),
                hrmAPI.getDepartments(),
                hrmAPI.getDesignations(),
                hrmAPI.getBranches(),
                hrmAPI.getAttendances(),
                hrmAPI.getLeaveApplications()
            ]);

            // CRM Data
            const [clientRes, projRes, liftRes] = await Promise.all([
                crmAPI.getClients(),
                crmAPI.getProjects(),
                crmAPI.getLifts()
            ]);

            // Inventory Data
            const [vendorRes, prodRes, storeRes, stockRes] = await Promise.all([
                vendorAPI.getVendors(),
                inventoryAPI.getProducts(),
                inventoryAPI.getStores(),
                inventoryAPI.getInventoryStocks()
            ]);

            // Finance Data
            const [coaRes, bankRes, journalRes] = await Promise.all([
                financeAPI.getChartOfAccounts(),
                financeAPI.getBankAccounts(),
                financeAPI.getJournalEntries()
            ]);

            setStats({
                employees: empRes.data.meta?.total || (empRes.data.data?.length || 0),
                departments: deptRes.data.meta?.total || (deptRes.data.data?.length || 0),
                designations: desigRes.data.meta?.total || (desigRes.data.data?.length || 0),
                branches: branchRes.data.meta?.total || (branchRes.data.data?.length || 0),
                attendances: attendRes.data.meta?.total || (attendRes.data.data?.length || 0),
                leaveApplications: leaveRes.data.meta?.total || (leaveRes.data.data?.length || 0),
                clients: clientRes.data.meta?.total || (clientRes.data.data?.length || 0),
                projects: projRes.data.meta?.total || (projRes.data.data?.length || 0),
                lifts: liftRes.data.meta?.total || (liftRes.data.data?.length || 0),
                vendors: vendorRes.data.meta?.total || (vendorRes.data.data?.length || 0),
                products: prodRes.data.meta?.total || (prodRes.data.data?.length || 0),
                stores: storeRes.data.meta?.total || (storeRes.data.data?.length || 0),
                inventoryStocks: stockRes.data.meta?.total || (stockRes.data.data?.length || 0),
                chartOfAccounts: coaRes.data.meta?.total || (coaRes.data.data?.length || 0),
                bankAccounts: bankRes.data.meta?.total || (bankRes.data.data?.length || 0),
                journalEntries: journalRes.data.meta?.total || (journalRes.data.data?.length || 0),
            });

            // Generate recent activities based on data
            setRecentActivities([
                { id: 1, type: 'employee', message: `${empRes.data.data?.length || 0} employees in system`, time: 'Just now', icon: Users, color: 'bg-blue-100 text-blue-600' },
                { id: 2, type: 'client', message: `${clientRes.data.data?.length || 0} active clients`, time: 'Just now', icon: Briefcase, color: 'bg-yellow-100 text-yellow-600' },
                { id: 3, type: 'project', message: `${projRes.data.data?.length || 0} ongoing projects`, time: 'Just now', icon: Building2, color: 'bg-green-100 text-green-600' },
                { id: 4, type: 'product', message: `${prodRes.data.data?.length || 0} products in inventory`, time: 'Just now', icon: Package, color: 'bg-purple-100 text-purple-600' },
            ]);

        } catch (err) {
            console.error('Failed to load stats:', err);
        } finally {
            setLoading(false);
        }
    };

    const exportDashboardData = () => {
        const data = [
            { module: 'HRM', metric: 'Employees', value: stats.employees },
            { module: 'HRM', metric: 'Departments', value: stats.departments },
            { module: 'HRM', metric: 'Designations', value: stats.designations },
            { module: 'HRM', metric: 'Branches', value: stats.branches },
            { module: 'CRM', metric: 'Clients', value: stats.clients },
            { module: 'CRM', metric: 'Projects', value: stats.projects },
            { module: 'CRM', metric: 'Lifts', value: stats.lifts },
            { module: 'Inventory', metric: 'Products', value: stats.products },
            { module: 'Inventory', metric: 'Stores', value: stats.stores },
            { module: 'Inventory', metric: 'Vendors', value: stats.vendors },
            { module: 'Finance', metric: 'Chart of Accounts', value: stats.chartOfAccounts },
            { module: 'Finance', metric: 'Bank Accounts', value: stats.bankAccounts },
            { module: 'Finance', metric: 'Journal Entries', value: stats.journalEntries },
        ];
        
        exportToExcel(data, 'dashboard_stats', [
            { key: 'module', label: 'Module' },
            { key: 'metric', label: 'Metric' },
            { key: 'value', label: 'Count' }
        ]);
    };

    // Main stat cards
    const statCards = [
        { id: 1, title: 'Employees', value: stats.employees, icon: Users, color: 'bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-200', trend: 5, link: '/hrm/employees' },
        { id: 2, title: 'Departments', value: stats.departments, icon: Building2, color: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-200', trend: 2, link: '/hrm/departments' },
        { id: 3, title: 'Clients', value: stats.clients, icon: Briefcase, color: 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-200', trend: -3, link: '/crm/clients' },
        { id: 4, title: 'Projects', value: stats.projects, icon: Building2, color: 'bg-green-50 text-green-600 dark:bg-green-900 dark:text-green-200', trend: 8, link: '/crm/projects' },
        { id: 5, title: 'Vendors', value: stats.vendors, icon: Truck, color: 'bg-red-50 text-red-600 dark:bg-red-900 dark:text-red-200', trend: 0, link: '/vendors' },
        { id: 6, title: 'Products', value: stats.products, icon: Package, color: 'bg-purple-50 text-purple-600 dark:bg-purple-900 dark:text-purple-200', trend: 12, link: '/inventory/products' },
        { id: 7, title: 'Stores', value: stats.stores, icon: Store, color: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900 dark:text-cyan-200', trend: 0, link: '/inventory/stores' },
        { id: 8, title: 'Bank Accounts', value: stats.bankAccounts, icon: CreditCard, color: 'bg-teal-50 text-teal-600 dark:bg-teal-900 dark:text-teal-200', trend: 0, link: '/finance/bank-accounts' },
    ];

    // Module summaries
    const hrmModules = [
        { name: 'Employees', count: stats.employees, link: '/hrm/employees', icon: Users },
        { name: 'Departments', count: stats.departments, link: '/hrm/departments', icon: Building2 },
        { name: 'Designations', count: stats.designations, link: '/hrm/departments', icon: Briefcase },
        { name: 'Branches', count: stats.branches, link: '/hrm/departments', icon: Building2 },
        { name: 'Attendances', count: stats.attendances || 0, link: '/hrm/attendances', icon: Calendar },
        { name: 'Leave Applications', count: stats.leaveApplications || 0, link: '/hrm/leave-applications', icon: ClipboardList },
        { name: 'Salary Structures', count: 0, link: '/hrm/salary-structures', icon: DollarSign },
    ];

    const crmModules = [
        { name: 'Clients', count: stats.clients, link: '/crm/clients', icon: Briefcase },
        { name: 'Projects', count: stats.projects, link: '/crm/projects', icon: Building2 },
        { name: 'Lifts', count: stats.lifts, link: '/crm/lifts', icon: Building2 },
    ];

    const inventoryModules = [
        { name: 'Stores', count: stats.stores, link: '/inventory/stores', icon: Store },
        { name: 'Products', count: stats.products, link: '/inventory/products', icon: Package },
        { name: 'Inventory Stocks', count: stats.inventoryStocks || 0, link: '/inventory/stocks', icon: Package },
        { name: 'Vendors', count: stats.vendors, link: '/vendors', icon: Truck },
    ];

    const financeModules = [
        { name: 'Chart of Accounts', count: stats.chartOfAccounts, link: '/finance/chart-of-accounts', icon: FileText },
        { name: 'Bank Accounts', count: stats.bankAccounts, link: '/finance/bank-accounts', icon: CreditCard },
        { name: 'Journal Entries', count: stats.journalEntries, link: '/finance/journal-entries', icon: ClipboardList },
    ];

    // Quick actions
    const quickActions = [
        { label: 'Add Employee', link: '/hrm/employees', icon: Users, color: 'bg-blue-500 text-white hover:bg-blue-600' },
        { label: 'Add Client', link: '/crm/clients', icon: Briefcase, color: 'bg-yellow-500 text-white hover:bg-yellow-600' },
        { label: 'Add Product', link: '/inventory/products', icon: Package, color: 'bg-purple-500 text-white hover:bg-purple-600' },
        { label: 'Add Vendor', link: '/vendors', icon: Truck, color: 'bg-red-500 text-white hover:bg-red-600' },
        { label: 'New Journal', link: '/finance/journal-entries', icon: FileText, color: 'bg-teal-500 text-white hover:bg-teal-600' },
        { label: 'New Project', link: '/crm/projects', icon: Building2, color: 'bg-green-500 text-white hover:bg-green-600' },
    ];

    // Calculate total records
    const totalRecords = stats.employees + stats.departments + stats.clients + 
                        stats.projects + stats.vendors + stats.products + 
                        stats.stores + stats.bankAccounts + stats.chartOfAccounts;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Admin Dashboard</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-300">Welcome back! Here's what's happening in your organization.</p>
                </div>
                <div className="mt-4 sm:mt-0 flex gap-2">
                    <button
                        onClick={fetchDashboardStats}
                        className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-gray-800 dark:text-gray-200 rounded-lg"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </button>
                    <button
                        onClick={exportDashboardData}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Export
                    </button>
                </div>
            </div>

            {/* Summary Stats Bar */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-4 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <Activity className="w-8 h-8 mr-3 opacity-80" />
                        <div>
                            <p className="text-2xl font-bold">{totalRecords.toLocaleString()}</p>
                            <p className="text-sm opacity-80">Total Records Across All Modules</p>
                        </div>
                    </div>
                    <div className="text-right hidden sm:block">
                        <p className="text-lg font-semibold">Benfix ERP</p>
                        <p className="text-sm opacity-80">Admin Panel</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
            ) : (
                <>
                    {/* Main Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {statCards.map(card => (
                            <StatCard key={card.id} {...card} />
                        ))}
                    </div>

                    {/* Module Sections */}
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                            <BarChart3 className="w-5 h-5 mr-2" />
                            Module Overview
                        </h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                            <ModuleCard 
                                title="HRM Module" 
                                icon={Users} 
                                color="bg-blue-100 text-blue-600"
                                modules={hrmModules}
                            />
                            <ModuleCard 
                                title="CRM Module" 
                                icon={Briefcase} 
                                color="bg-yellow-100 text-yellow-600"
                                modules={crmModules}
                            />
                            <ModuleCard 
                                title="Inventory Module" 
                                icon={Package} 
                                color="bg-purple-100 text-purple-600"
                                modules={inventoryModules}
                            />
                            <ModuleCard 
                                title="Finance Module" 
                                icon={DollarSign} 
                                color="bg-teal-100 text-teal-600"
                                modules={financeModules}
                            />
                        </div>
                    </div>

                    {/* Quick Actions & Recent Activity */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Quick Actions */}
                        <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-5">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                                <Activity className="w-5 h-5 mr-2" />
                                Quick Actions
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {quickActions.map((action, idx) => (
                                    <QuickActionButton key={idx} {...action} />
                                ))}
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-5">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                                <Clock className="w-5 h-5 mr-2" />
                                System Status
                            </h3>
                            <div className="space-y-3">
                                {recentActivities.map(activity => (
                                    <div key={activity.id} className="flex items-center p-3 bg-gray-50 dark:bg-slate-600 rounded-lg">
                                        <div className={`p-2 rounded-full ${activity.color}`}>
                                            <activity.icon className="w-4 h-4" />
                                        </div>
                                        <div className="ml-3 flex-1">
                                            <p className="text-sm text-gray-800 dark:text-gray-100">{activity.message}</p>
                                            <p className="text-xs text-gray-500">{activity.time}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* All Modules Link Cards */}
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                            <PieChart className="w-5 h-5 mr-2" />
                            All Modules
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                            <Link to="/hrm" className="bg-blue-50 dark:bg-slate-700 p-4 rounded-lg text-center hover:bg-blue-100 dark:hover:bg-slate-600 transition-colors">
                                <Users className="w-8 h-8 mx-auto text-blue-600 dark:text-blue-300 mb-2" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">HRM</span>
                            </Link>
                            <Link to="/crm" className="bg-yellow-50 dark:bg-slate-700 p-4 rounded-lg text-center hover:bg-yellow-100 dark:hover:bg-slate-600 transition-colors">
                                <Briefcase className="w-8 h-8 mx-auto text-yellow-600 dark:text-yellow-300 mb-2" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">CRM</span>
                            </Link>
                            <Link to="/inventory" className="bg-purple-50 dark:bg-slate-700 p-4 rounded-lg text-center hover:bg-purple-100 dark:hover:bg-slate-600 transition-colors">
                                <Package className="w-8 h-8 mx-auto text-purple-600 dark:text-purple-300 mb-2" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Inventory</span>
                            </Link>
                            <Link to="/finance" className="bg-teal-50 dark:bg-slate-700 p-4 rounded-lg text-center hover:bg-teal-100 dark:hover:bg-slate-600 transition-colors">
                                <DollarSign className="w-8 h-8 mx-auto text-teal-600 dark:text-teal-300 mb-2" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Finance</span>
                            </Link>
                            <Link to="/vendors" className="bg-red-50 dark:bg-slate-700 p-4 rounded-lg text-center hover:bg-red-100 dark:hover:bg-slate-600 transition-colors">
                                <Truck className="w-8 h-8 mx-auto text-red-600 dark:text-red-300 mb-2" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Vendors</span>
                            </Link>
                            <Link to="/settings" className="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg text-center hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors">
                                <Building2 className="w-8 h-8 mx-auto text-gray-600 dark:text-gray-300 mb-2" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Settings</span>
                            </Link>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
